import { useEffect, useRef, useState, FormEvent, ChangeEvent } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { VoteStatus } from "types/voteEnum";
import { sendTagCloudToDisplay } from "components/helper/callApi";
import { useUser } from "components/helper/userContext";
import { frameFillBackground } from "components/common/backgroundFrame";
import { textFieldFocus } from "globalStyles";
import { useParams } from "react-router-dom";
import { HTTPResponseError } from "services/errors";

import ActionSection from "../common/ActionSection";
import Spinner from "../../../common/Spinner";
import TitleHeader from "../common/TitleHeader";
import { FocusAfterSending } from "./types";

type Props = {
    onValidate: any;
    title: string;
    numberOfInputs: number;
    userCanSubmitMultipleTimes: boolean;
};

const TagCloudQuestion = ({
    title,
    onValidate,
    numberOfInputs,
    userCanSubmitMultipleTimes,
}: Props) => {
    //Transform the numberOfInputs to array
    const initialEmptyArray = Array(numberOfInputs).fill("");

    const { onlineCode } = useParams();
    const { user } = useUser();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [answers, setAnswers] = useState<Array<string>>(initialEmptyArray);
    const [voteStatus, setVoteStatus] = useState<VoteStatus>(VoteStatus.Idle);
    const [answersHaveAlreadyBeenSent, setAnswersHaveAlreadyBeenSent] =
        useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const wordsSentToCloud = useRef<string | null>(null);
    // The spinner replaces the question while sending: where to put the focus
    // back once it is done, so it does not fall to the top of the page
    const focusAfterSendingRef = useRef<FocusAfterSending | null>(null);
    const confirmationRef = useRef<HTMLParagraphElement>(null);
    const firstFieldRef = useRef<HTMLInputElement>(null);

    const answersIsEmpty = answers.every((answer) => 0 === answer.length);

    useEffect(() => {
        if (answersIsEmpty) {
            setVoteStatus(VoteStatus.Idle);
        } else {
            setVoteStatus(VoteStatus.PendingValidation);
        }
    }, [answersIsEmpty]);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        const focusTarget = focusAfterSendingRef.current;
        focusAfterSendingRef.current = null;
        if ("confirmation" === focusTarget) {
            // Focusing the confirmation also has it read (RGAA 7.5)
            confirmationRef.current?.focus();
        } else if ("field" === focusTarget) {
            firstFieldRef.current?.focus();
        }
    }, [isLoading]);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        handleValidate();
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const newAnswers = [...answers];
        newAnswers[index] = e.target.value;

        setVoteStatus(VoteStatus.PendingValidation);
        setAnswers(newAnswers);
    };

    const sendToTagsCloud = async (answers: Array<string>) => {
        const answersFiltered = answers.filter((answer) => answer !== "");

        const response = {
            words: answersFiltered,
            id: user?.id,
        };

        return sendTagCloudToDisplay(onlineCode as string, response).catch(
            (error) => {
                if (!(error instanceof HTTPResponseError)) {
                    enqueueSnackbar(`${t("errorUnknown")}`);
                }

                const res = error.response;

                if (res.status === 400) {
                    enqueueSnackbar(`${t("errorFieldsEmpty")}`, {
                        variant: "error",
                    });
                } else {
                    enqueueSnackbar(`${t("errorUnknown")}`, {
                        variant: "error",
                    });
                }

                throw error;
            },
        );
    };

    const handleValidate = async () => {
        setIsLoading(true);
        focusAfterSendingRef.current = "field";
        const answersKey = JSON.stringify(answers);
        try {
            // On a retry after a failed answer, the cloud already shows these
            // words: don't send them twice
            if (wordsSentToCloud.current !== answersKey) {
                await sendToTagsCloud(answers);
                wordsSentToCloud.current = answersKey;
            }
            try {
                await onValidate(answers);
                setAnswersHaveAlreadyBeenSent(true);
                focusAfterSendingRef.current = "confirmation";
                // Clear the words only once they are sent, so the user can retry
                setAnswers(initialEmptyArray);
                wordsSentToCloud.current = null;
            } catch {
                setVoteStatus(VoteStatus.Error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const renderTagCloud = () => {
        return (
            <>
                {answersHaveAlreadyBeenSent ? (
                    <StyledParagraph ref={confirmationRef} tabIndex={-1}>
                        {t("responseHasBeenConsidered")},
                        {t("youCanResendResponse")}
                    </StyledParagraph>
                ) : null}
                <WrapperForm>
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div>
                            {initialEmptyArray.map((input, index) => {
                                const inputRef =
                                    0 === index ? firstFieldRef : undefined;

                                return (
                                    <StyledInput
                                        ref={inputRef}
                                        value={answers[index] || ""}
                                        onChange={(e) => handleChange(e, index)}
                                        type={"text"}
                                        maxLength={30}
                                        placeholder={t("enterWord") as string}
                                        aria-label={t("tagCloudWord", {
                                            number: index + 1,
                                        })}
                                        key={index}
                                    />
                                );
                            })}
                        </div>
                    </form>
                </WrapperForm>
                <ActionSection
                    voteStatus={voteStatus}
                    handleValidate={handleValidate}
                    canAnswerBeUpdated={true}
                />
            </>
        );
    };

    return isLoading ? (
        <SpinnerWrapper>
            <Spinner label={t("loading")} />
        </SpinnerWrapper>
    ) : (
        <WrapperVote>
            <TitleHeader title={title} />
            {!userCanSubmitMultipleTimes && answersHaveAlreadyBeenSent ? (
                <StyledParagraph ref={confirmationRef} tabIndex={-1}>
                    {`${t("responseHasBeenConsidered")}`}
                </StyledParagraph>
            ) : (
                renderTagCloud()
            )}
        </WrapperVote>
    );
};

const WrapperVote = styled.div`
    width: 100%;
    max-width: 620px;
    margin: auto;
    @media (max-width: 768px) {
        max-width: 350px;
    }
`;

const WrapperForm = styled.div`
    text-align: center;
    margin-top: 1.5rem;
`;

const StyledInput = styled.input`
    margin: 0.75rem auto;
    width: 90%;
    height: 25px;
    padding: 6px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 15px;
    ${frameFillBackground}
    &:focus {
        ${textFieldFocus()}
    }
    &[disabled] {
        background: #e8e8e8;
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

const StyledParagraph = styled.p`
    margin-top: 2.5rem;
`;

const SpinnerWrapper = styled.div`
    padding-bottom: 20px;
`;

export default TagCloudQuestion;
